/**
 * A VM's controls in Machines (ADR 0106 decision 3): its menu items above the machine's common
 * ones, the Stop and Delete confirmations, the card line that says how it ends and what it costs,
 * the drawer section, and the card of a VM whose machine has not joined yet.
 */
import { Bookmark, CirclePlay, CircleStop, Cloud, RotateCw, Trash2 } from '@orgus/ui/icons';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { cloudVmEnding } from '@orgus/protocol';
import type { CloudVm } from '@orgus/protocol';
import { Badge, ConfirmDialog, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, ErrorState, IconButton, RowStatus, Select, cn, toUiError } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { Ellipsis } from '@orgus/ui/icons';
import { deleteVm, setIdleStop, vmAction } from './data.ts';
import { IDLE_STOP_OPTIONS, deleteVmText, vmCostText, vmFacts, vmMenuItems, vmRowStatus } from './model.ts';
import type { VmMenuKey } from './model.ts';

/** A VM action that failed, for the page's banner. */
export type VmFailure = { what: string; error: unknown };

const ICON: Record<VmMenuKey, ReactNode> = { stop: <CircleStop />, start: <CirclePlay />, restart: <RotateCw />, keep: <Bookmark />, delete: <Trash2 /> };
const FAILED: Record<VmMenuKey, string> = { stop: 'was not stopped', start: 'was not started', restart: 'was not restarted', keep: 'was not kept', delete: 'was not deleted' };

/** Which confirmation is open: Stop and Delete ask first. */
type Asking = 'stop' | 'delete' | null;

/**
 * The VM's items for a machine's menu (render inside its DropdownMenuContent, above the common
 * items) and the confirmations they open (render beside the menu).
 */
export function useVmMenu(vm: CloudVm | null, o: { running: number; onFail: (f: VmFailure) => void; separator?: boolean }) {
  const [asking, setAsking] = useState<Asking>(null);
  if (!vm) return { items: null, dialogs: null };
  const run = (key: VmMenuKey) => {
    if (key === 'stop' && o.running > 0) return setAsking('stop');
    if (key === 'delete') return setAsking('delete');
    void vmAction(vm, key as 'stop' | 'start' | 'restart' | 'keep').catch((error: unknown) => o.onFail({ what: `${vm.name} ${FAILED[key]}`, error }));
  };
  const items = (
    <>
      {vmMenuItems(vm).map((it) => (
        <DropdownMenuItem key={it.key} icon={ICON[it.key]} danger={it.key === 'delete'} disabled={it.disabled} onSelect={() => run(it.key)}>
          {it.label}
        </DropdownMenuItem>
      ))}
      {o.separator !== false && <DropdownMenuSeparator />}
    </>
  );
  const dialogs = (
    <>
      <ConfirmDialog
        open={asking === 'stop'}
        onOpenChange={(v) => !v && setAsking(null)}
        title={`Stop ${vm.name}?`}
        description={`New work stops now. ${o.running === 1 ? 'The running item finishes' : `The ${o.running} running items finish`} first, for up to 10 minutes, then AWS stops the VM. Its disk is kept; Start VM brings it back.`}
        confirmLabel="Stop VM"
        cancelLabel="Keep running"
        onConfirm={async () => {
          await vmAction(vm, 'stop', { waitMinutes: 10 });
        }}
      />
      <ConfirmDialog
        open={asking === 'delete'}
        onOpenChange={(v) => !v && setAsking(null)}
        title={`Delete ${vm.name}?`}
        description={deleteVmText(vm, o.running)}
        confirmLabel="Delete VM"
        cancelLabel="Keep VM"
        tone="danger"
        onConfirm={async () => {
          await deleteVm(vm);
        }}
      />
    </>
  );
  return { items, dialogs };
}

/** The card's status while the VM is changing or stopped; null: the machine's own status applies. */
export function VmStatus({ vm, machineOnline, className }: { vm: CloudVm; machineOnline: boolean; className?: string }) {
  const s = vmRowStatus(vm, machineOnline);
  return s ? <RowStatus {...s} className={className} /> : null;
}

/** "Deleted in 22m if idle, ~$0.04 an hour" (stopped: "~$1.60 a month for its disk"): how the VM ends and what it costs now. */
export function vmLine(vm: CloudVm, now: number): string | null {
  const parts = [cloudVmEnding(vm, now), vmCostText(vm)].filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

/** The lifetime chip: Ephemeral or Permanent. */
export function LifetimeChip({ vm }: { vm: Pick<CloudVm, 'lifetime'> }) {
  return (
    <Badge tone="neutral" variant="soft" size="sm">
      {vm.lifetime === 'ephemeral' ? 'Ephemeral' : 'Permanent'}
    </Badge>
  );
}

/** The drawer's VM section: provider, region and size, lifetime, estimate, and any provider error. */
export function VmDetails({ vm, now }: { vm: CloudVm | null; now: number }) {
  if (!vm) {
    return (
      <section aria-label="Cloud VM" className="flex flex-col gap-1">
        <p className="text-ui font-medium text-fg">Cloud VM</p>
        <p className="text-caption text-fg-subtle">Orgus does not control this machine&apos;s VM. In AWS: tag the instance orgus=fleet, then adopt it from Machines. Elsewhere, stop or delete it in its provider&apos;s console.</p>
      </section>
    );
  }
  const line = vmLine(vm, now);
  return (
    <section aria-label="Cloud VM" className="flex flex-col gap-2" data-testid="vm-details">
      <div className="flex items-center gap-3">
        <p className="text-ui font-medium text-fg">Cloud VM</p>
        <LifetimeChip vm={vm} />
      </div>
      <p className="text-caption text-fg-muted">{vmFacts(vm)}</p>
      <p className="font-mono text-caption text-fg-subtle">{vm.instanceId}</p>
      {line && <p className="text-caption text-fg-subtle num">{line}</p>}
      {vm.lifetime === 'permanent' && <IdleStop vm={vm} />}
      {vm.error && <p className="text-caption text-status-danger-ink">{vm.error}</p>}
    </section>
  );
}

/** A permanent VM's Stop when idle rule (ADR 0106 decision 5): never by default. */
function IdleStop({ vm }: { vm: CloudVm }) {
  const [error, setError] = useState<UiError | null>(null);
  const value = vm.idleStopMs ? String(Math.round(vm.idleStopMs / 60_000)) : 'never';
  const options = IDLE_STOP_OPTIONS.some((o) => o.value === value) ? IDLE_STOP_OPTIONS : [...IDLE_STOP_OPTIONS, { value, label: `After ${value} minutes idle` }];
  return (
    <div className="flex flex-col gap-1" data-testid="vm-idle-stop">
      <Select
        label="Stop when idle"
        value={value}
        options={options}
        onValueChange={(v) => {
          setError(null);
          void setIdleStop(vm, v === 'never' ? null : Number(v)).catch((e: unknown) => setError(toUiError(e, { what: "The VM's idle rule was not saved." })));
        }}
      />
      {error && <ErrorState variant="inline" error={error} />}
    </div>
  );
}

/** A VM Orgus created whose machine has not joined yet (or has left): its own card, Starting or Stopped. */
export function VmCard({ vm, now, onFail, className }: { vm: CloudVm; now: number; onFail: (f: VmFailure) => void; className?: string }) {
  const menu = useVmMenu(vm, { running: 0, onFail, separator: false });
  const line = vmLine(vm, now);
  return (
    <article aria-label={`${vm.name}, a cloud VM`} className={cn('flex h-full flex-col gap-4 p-4', className)} data-testid="vm-card">
      <header className="flex min-w-0 items-center gap-3">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-control bg-raised text-fg-subtle" aria-hidden>
          <Cloud className="size-4" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex min-w-0 items-center gap-3">
            <h3 className="min-w-0 flex-1 truncate text-ui font-medium text-fg">{vm.name}</h3>
            <VmStatus vm={vm} machineOnline={false} className="shrink-0" />
          </div>
          <p className="min-w-0 truncate text-caption text-fg-subtle">{vmFacts(vm)}</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <IconButton label={`More for ${vm.name}`} icon={<Ellipsis />} size="sm" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">{menu.items}</DropdownMenuContent>
        </DropdownMenu>
      </header>
      <p className="text-caption text-fg-subtle">
        {vm.state === 'stopped' ? 'Stopped before it joined. Start VM to bring it up.' : 'Installing the agent. It joins by itself, usually within two minutes of boot.'}
      </p>
      <footer className="mt-auto flex min-w-0 items-center gap-3">
        <LifetimeChip vm={vm} />
        {line && <span className="min-w-0 truncate text-caption text-fg-subtle num">{line}</span>}
      </footer>
      {menu.dialogs}
    </article>
  );
}
